import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { formatIrr } from '../format'

interface Props {
  /** Rials. `null` means nobody has configured this yet — not zero. */
  amountIrr: number | null | undefined
  /**
   * What absence means *here*. Required, with no default, because the answer
   * differs by column: an unpriced operation and an uncosted route are
   * different facts and neither of them is «۰ ریال».
   */
  whenUnknown: string
  /** Longer explanation shown on hover, where the short form needs one. */
  unknownHint?: string
  /** Let a caller state the amount louder than body text, where the amount is
   *  the point of the block it sits in. */
  emphasis?: boolean
}

/**
 * One rial amount on screen.
 *
 * The single place a possibly-absent money value becomes text. `formatIrr`
 * stays strict and throws on anything that is not an integer, because a
 * fractional rial is an arithmetic bug worth surfacing — but a page must never
 * hand it a value that is legitimately absent, and most money on this platform
 * starts absent.
 *
 * Rendering the unknown case as a muted placeholder rather than a number is
 * the point. An operator who sees «۰ ریال» reasonably concludes the service is
 * free; the truth is that nobody has priced it yet, and those lead to
 * different decisions.
 */
export function Money({ amountIrr, whenUnknown, unknownHint, emphasis = false }: Props) {
  if (amountIrr === null || amountIrr === undefined) {
    const placeholder = (
      <Typography
        component="span"
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: 'italic' }}
      >
        {whenUnknown}
      </Typography>
    )
    return unknownHint ? <Tooltip title={unknownHint}>{placeholder}</Tooltip> : placeholder
  }

  return (
    <Typography
      component="span"
      variant={emphasis ? 'subtitle1' : 'body2'}
      sx={{
        fontVariantNumeric: 'tabular-nums',
        ...(emphasis ? { fontWeight: 600 } : {}),
        whiteSpace: 'nowrap',
      }}
    >
      {formatIrr(amountIrr)}
    </Typography>
  )
}

/**
 * A published price range, which is absent as a pair or not at all.
 *
 * Ranges were the worst offender for `?? 0`: a missing lower bound rendered as
 * «۰ ریال – ۵۰٬۰۰۰ ریال», which reads as a real range starting at free.
 */
export function MoneyRange({
  minIrr,
  maxIrr,
  whenUnknown,
}: {
  minIrr: number | null | undefined
  maxIrr: number | null | undefined
  whenUnknown: string
}) {
  if (minIrr === null || minIrr === undefined || maxIrr === null || maxIrr === undefined) {
    return <Money amountIrr={null} whenUnknown={whenUnknown} />
  }
  return (
    <Typography component="span" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {formatIrr(minIrr)} – {formatIrr(maxIrr)}
    </Typography>
  )
}
